// import { format, parseISO } from 'date-fns'
import { AbsoluteFill } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { FallbackImg } from '../components/FallbackImg'
import { BarsVisualization } from '../components/waveform/BarsVisualization'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { useOrientation } from '../utils/useOrientation'
import { sceneAudioSrc } from '../utils/sceneAudioSrc'

function Postcard({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { p, s, l, lp } = useOrientation(orientation)

  return (
    <AbsoluteFill
      className={cn(p && 'py-[160px]', s && 'px-[140px]', l && 'px-[175px]')}
    >
      {clipScene.showZealousLogo && (
        <ZealousSvg
          onlyIcon
          color={style.logo.color}
          className={cn(
            'absolute h-[48px] opacity-50',
            p && 'right-[56px] bottom-[56px]',
            s && 'right-[46px] bottom-[36px]',
            l && 'right-[63px] bottom-[63px]',
          )}
        />
      )}

      <div
        className={cn(
          'flex h-full w-full flex-col items-center justify-between text-center',
          p && 'px-[103px] py-[100px]',
          s && 'px-[76px] py-[39px]',
          l && 'px-[100px] py-[70px]',
        )}
        style={{ background: style.border.color }}
      >
        <div className="flex flex-col">
          <p
            style={style.subtitle}
            className={cn(
              'mb-4 line-clamp-2 text-[40px] leading-[132%]',
              s && 'text-[32px]',
              l && 'line-clamp-1',
            )}
          >
            {clipScene.subtitle}
          </p>

          <p
            style={style.title}
            className={cn(
              'line-clamp-2 text-[60px] leading-[132%] italic',
              s && 'text-[52px]',
            )}
          >
            {clipScene.title}
          </p>
        </div>

        <div className="w-full max-w-[900px]">
          <div className="relative flex w-full justify-center">
            <div className="absolute top-0 left-0 flex h-full w-full justify-center">
              <BarsVisualization
                className="h-full w-full"
                color={style.waveform.color}
                gapSize={10}
                lineThickness={2}
                mediaStart={clipScene.mediaStart}
                mediaUrl={sceneAudioSrc(clipScene)}
                waveformUrl={clipScene.waveformUrl}
                mirror
              />
            </div>

            <FallbackImg
              className={cn(
                'relative aspect-square h-[216px] rounded-full object-cover',
                p && 'h-[308px]',
                l && 'h-[220px]',
              )}
              src={speaker?.avatarUrl}
            />
          </div>

          <p
            style={style.speakerUsername}
            className={cn(
              'mt-[40px] line-clamp-1 text-[36px]',
              p && 'mt-[61px] text-[54px]',
              l && 'text-[44px]',
            )}
          >
            {speaker?.username}
          </p>
        </div>

        <div>{children}</div>
      </div>
    </AbsoluteFill>
  )
}

export const postcardLayoutInfo: LayoutInfo = {
  name: 'postcard',
  Component: Postcard,
  defaultScene: {
    ...defaultClipScene,
    layout: 'postcard',
  },
  defaultStyle: {
    background: { color: 'accent' },
    border: { color: 'primary' },
    logo: { color: 'primary' },
    waveform: { color: 'accent' },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    title: { color: 'secondary', fontFamily: 'primary' },
    speakerUsername: { color: 'secondary', fontFamily: 'primary' },
    transcription: {
      highlightColor: 'accent',
      color: 'secondary',
      fontFamily: 'primary',
      highlightType: 'last-word',
      unPlayedColor: 'secondary',
      unPlayedOpacity: 0.5,
      fontSize: 62,
      fontWeight: 400,
      scrollType: 'page-by-page',
      linesPerPage: 3,
      lineHeight: 1.14,
      textAlign: 'center',
    },
    date: {
      color: 'secondary',
      fontFamily: 'Red Hat Display',
      opacity: 0.5,
    },
  },
  transcriptions: {
    square: {},
    landscape: {
      fontSize: 73,
      lineHeight: 1.4,
      linesPerPage: 2,
    },
    portrait: {
      fontSize: 80,
      lineHeight: 1.42,
    },
  },
}
